// vim: ft=javascriptreact
import { useState, useEffect } from 'react'
import {
  Alert,
  Col,
  Button,
  ButtonGroup,
  Form,
  Image,
  Modal,
  Row,
  Table,
  ToggleButton,
} from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import useBooking from '../hooks/useBooking'
import useIsAdmin from '../hooks/useIsAdmin'
import Success from './Success'
import Terms from '../Terms'

function BookingForm() {
  const navigate = useNavigate()
  const { date } = useParams()
  const { createBooking, refreshBookings } = useBooking()
  const [error, setError] = useState('')
  const [showTerms, setShowTerms] = useState(false)
  const [saving, setSaving] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [agreeToFine, setAgreeToFine] = useState(false)
  const [agreeToLiabilityWaiver, setAgreeToLiabilityWaiver] = useState(false)
  const [notes, setNotes] = useState('')
  const [bookingComplete, setBookingComplete] = useState(false)
  const [bookingConfirmed, setBookingConfirmed] = useState(false)
  const isAdmin = useIsAdmin()
  const [officialUse, setOfficialUse] = useState(false)
  const [officialUseZoom, setOfficialUseZoom] = useState(false)
  const [title, setTitle] = useState('')

  const onBookingFormComplete = () => {
    setError('')
    setSaving(true)
    createBooking(date, notes, title, officialUse, officialUseZoom)
      .then((payload) => {
        if (payload.confirmed) {
          setBookingConfirmed(true)
        }
        setBookingComplete(true)
        refreshBookings()
      })
      .catch((err) => {
        console.log('wth', err)
        setError(err)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  useEffect(() => {
    if (officialUse && !title) {
      setTitle('Council Meeting')
    } else if (officialUse && officialUseZoom) {
      setTitle('Council Meeting (virtual)')
    }
  }, [officialUse, officialUseZoom])

  const dateFormatted = format(parseISO(date), 'EEEE, LLLL do, yyyy')
  const canBook = officialUse ? (title.length > 5) : (agreeToTerms && agreeToFine && agreeToLiabilityWaiver)

  if (bookingComplete) {
    return (
      <Success date={dateFormatted} officialUse={officialUse} isConfirmed={bookingConfirmed} />
    )
  }

  return (
    <Row>
      <Col md={7} lg={8}>
        <h2>Booking Form</h2>
        {isAdmin && (
          <div className="float-end">
            <ButtonGroup className="mb-3">
              <ToggleButton
                id="official-use"
                type="checkbox"
                variant="outline-secondary"
                checked={officialUse}
                onChange={() => setOfficialUse(!officialUse)}
              >
                Official Strata Use
              </ToggleButton>
            </ButtonGroup>
            {officialUse && (
              <ButtonGroup className="mb-3 ms-2">
                <ToggleButton
                  id="official-use-zoom"
                  type="checkbox"
                  variant="outline-secondary"
                  checked={officialUseZoom}
                  onChange={() => setOfficialUseZoom(!officialUseZoom)}
                >
                  Zoom?
                </ToggleButton>
              </ButtonGroup>
            )}
          </div>
        )}
        <h3>{dateFormatted}</h3>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>
        )}

        <Modal show={showTerms} onHide={() => setShowTerms(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Terms of Use</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Terms colProps={{}} includeTitle={false} />
          </Modal.Body>
        </Modal>

        { !officialUse && (
          <Table striped bordered>
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Rental of Main Floor</td>
                <td>$50</td>
              </tr>
            </tbody>
          </Table>
        )}

        <Form>
          {!officialUse && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="agree-to-terms"
                  label={
                    <span>I have read and agree to adhere to all of the <a className="btn-link" onClick={() => setShowTerms(true)}>Terms of Use</a> for the Amenities Room.</span>
                  }
                  checked={agreeToTerms}
                  onChange={({ target }) => setAgreeToTerms(target.checked)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="agree-to-fine"
                  label="I understand that the owner of the strata lot could be fined and billed for damages, repairs and/or cleaning if I or any other person attending a function is in breach of the terms and conditions stipulated herein. For tenants, these costs may be passed onto you by the Strata or owner."
                  checked={agreeToFine}
                  onChange={({ target }) => setAgreeToFine(target.checked)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="agree-to-liability-waiver"
                  label="I understand and agree that the Strata and all companies and peoples associated with the amenity building will not be held liable for any personal injuries that may occur to any individual (including guests, contractors, caterers, other vendors hired, etc.) during the rental hours when the building is the responsibility of resident(s) renting the building."
                  checked={agreeToLiabilityWaiver}
                  onChange={({ target }) => setAgreeToLiabilityWaiver(target.checked)}
                />
              </Form.Group>
            </>
          )}

          {officialUse && (
            <Form.Group className="mb-3">
              <Form.Label>Short Title For Calendar</Form.Label>
              <Form.Control placeholder="e.g. Council Meeting" rows={5} value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Additional notes or comments?</Form.Label>
            <Form.Control as="textarea" placeholder="Optional notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Group>


            <Button
              className="me-2"
              variant="outline-secondary"
              onClick={() => navigate('/schedule')}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              disabled={saving || !canBook}
              onClick={onBookingFormComplete}
            >
              { saving ? 'Requesting...' : (officialUse ? 'Create Booking' : 'Request Booking') }
            </Button>
        </Form>
      </Col>

      <Col lg={4} md={5} className="mt-5">
        <Image src="https://theimperialstrata.com/community/amenities-building/main.png" fluid />
      </Col>
    </Row>
  )
}

export default BookingForm
